import { createSelector } from "@reduxjs/toolkit";
import type { IPixPolygon, RoomSectionRenderItem } from "@multiconsult-group/multiplacer-ui";
import type { IRootState } from "../app/store";
import { selectFinalPixPolygons } from "../DesignTab/pixpolygonSelectors";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";
import { createDeviceCeilingHeightResolver, type CeilingHeightSpace } from "./deviceCeilingHeight";

export const VALIDATION_TILE_3D_ID_PREFIX = "validation-tile:";
const THICKNESS_M = 0.005;

export const buildValidationTile3dItems = (
  polygons: Pick<IPixPolygon, "id" | "points" | "fillColor">[],
  roomNumber: string | null | undefined,
  spaces: CeilingHeightSpace[],
  isMoving: boolean
): RoomSectionRenderItem[] => {
  if (!isMoving || !roomNumber) return [];
  const getHeight = createDeviceCeilingHeightResolver(spaces.filter(space => space.name === roomNumber));
  return polygons.flatMap((polygon) => {
    if (
      !polygon.id.startsWith("validation-tile-") ||
      !polygon.fillColor ||
      polygon.points.length < 4 ||
      !polygon.points.every((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]))
    )
      return [];
    const [a, b, , d] = polygon.points;
    const x = (b[0] + d[0]) / 2,
      y = (b[1] + d[1]) / 2;
    const height = getHeight({ coordinate: [x, y], roomNumber });
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const width = Math.hypot(d[0] - a[0], d[1] - a[1]);
    if (height == null || !Number.isFinite(height) || length <= 0 || width <= 0) return [];
    return [
      {
        item: {
          _id: `${VALIDATION_TILE_3D_ID_PREFIX}${roomNumber}:${polygon.id}`,
          geometry: {
            Obb3DWorldMmList: [
              {
                centerM: { x, y, z: height + THICKNESS_M / 2 },
                axisA: { x: (b[0] - a[0]) / length, y: (b[1] - a[1]) / length, z: 0 },
                axisB: { x: (d[0] - a[0]) / width, y: (d[1] - a[1]) / width, z: 0 },
                axisC: { x: 0, y: 0, z: 1 },
                extentAM: length / 2,
                extentBM: width / 2,
                extentCM: THICKNESS_M / 2,
              },
            ],
          },
        },
        color: polygon.fillColor,
        opacity: 0.85,
      },
    ];
  });
};

// Consume the visible 2D overlays so validation rules and visibility stay identical.
export const selectValidationTile3dItems = createSelector(
  [
    selectFinalPixPolygons,
    (state: IRootState) => state.design.selectedSpaceNumber,
    selectHttpMcbimSpacesResult,
    (state: IRootState) => state.design.deviceOnTheMove != null,
  ],
  buildValidationTile3dItems
);
